import type { Evidence } from "@/types/aicheck";

export interface EvidenceTraceEntry {
  path: string;
  globalIndex: number;
  evidence: Evidence;
}

function isEvidenceLike(item: unknown): item is Evidence {
  return (
    typeof item === "object" &&
    item !== null &&
    "source" in item &&
    typeof (item as Record<string, unknown>).source === "string"
  );
}

export function parseEvidenceArray(value: unknown): Evidence[] | null {
  let arr: unknown[] | null = null;
  if (Array.isArray(value)) {
    arr = value;
  } else if (typeof value === "string" && value.startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        arr = parsed;
      }
    } catch {
      return null;
    }
  }

  if (!arr) return null;
  return arr.every(isEvidenceLike) ? (arr as Evidence[]) : null;
}

function collectEvidenceEntriesInternal(
  value: unknown,
  path: string,
  entries: EvidenceTraceEntry[],
  limit: number,
) {
  if (entries.length >= limit) return;

  const evidenceArray = parseEvidenceArray(value);
  if (evidenceArray) {
    const remaining = limit - entries.length;
    for (const evidence of evidenceArray.slice(0, remaining)) {
      entries.push({
        path,
        globalIndex: entries.length,
        evidence,
      });
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      collectEvidenceEntriesInternal(item, `${path}[${index}]`, entries, limit);
    });
    return;
  }

  if (typeof value !== "object" || value === null) {
    return;
  }

  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const nextPath = path ? `${path}.${key}` : key;
    collectEvidenceEntriesInternal(child, nextPath, entries, limit);
    if (entries.length >= limit) {
      return;
    }
  }
}

export function collectEvidenceEntries(
  value: unknown,
  limit = Number.POSITIVE_INFINITY,
): EvidenceTraceEntry[] {
  const entries: EvidenceTraceEntry[] = [];
  collectEvidenceEntriesInternal(value, "", entries, limit);
  return entries;
}

export function buildEvidencePathOffsetMap(
  value: unknown,
): Map<string, number> {
  const offsets = new Map<string, number>();

  for (const entry of collectEvidenceEntries(value)) {
    if (!offsets.has(entry.path)) {
      offsets.set(entry.path, entry.globalIndex);
    }
  }

  return offsets;
}
